import { Box, Typography, Container, Button } from "@mui/material";
import LinearProgress, {
  linearProgressClasses,
} from "@mui/material/LinearProgress";
import { styled } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 20,
  borderRadius: 100,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: "#FFFFFF",
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 100,
    backgroundColor: "#FFCC4C",
  },
}));

const ResultExercise = () => {
  const [resultExercise, setResultExercise] = useState<any>({});
  const [percent, setPercent] = useState<any>({});
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!location.state) {
      navigate(`/home`);
      return;
    }
    const { experience_point, next_experience_point } = location.state;
    setPercent((experience_point * 100) / next_experience_point);
    setResultExercise(location.state);
  }, [location, navigate]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: "30px",
        py: "50px",
      }}
    >
      <Typography
        align="center"
        sx={{ typography: { xs: "h3", sm: "h1" } }}
        variant="h1"
        color="ktgGreen.main"
      >
        <FormattedMessage id="exercise.sendResultSuccess" />
      </Typography>

      <Container
        component="main"
        sx={{
          zIndex: 1000,
          maxWidth: 600,
          gap: "15px",
          display: "flex",
          flexDirection: "column",
        }}
        maxWidth={false}
      >
        <Box
          sx={{
            boxShadow: "0px 0px 6px rgba(0, 0, 0, 0.15)",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            padding: "40px 25px",
            borderRadius: "10px",
            background:
              "linear-gradient(94.1deg, rgba(4, 205, 133, 0.9) -20.26%, rgba(28, 185, 243, 0.9) 149.71%)",
          }}
        >
          <Typography align="center" variant="h5" color="common.white">
            <FormattedMessage id="exercise.powerUsed" />
          </Typography>

          <Box
            sx={{
              background: "#FFFFFF",
              height: 80,
              border: "1px solid #D9E2EB",
              borderRadius: "100px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography align="center" variant="h2" color="secPink.main">
              {resultExercise?.calories}
            </Typography>
          </Box>

          <Typography align="center" variant="body1" color="common.white">
            <FormattedMessage id="exercise.kiloCalories" />
          </Typography>
        </Box>

        <Box
          sx={{
            boxShadow: "0px 0px 6px rgba(0, 0, 0, 0.15)",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            padding: "40px 25px",
            borderRadius: "10px",
            background:
              "linear-gradient(142.14deg, rgba(55, 224, 174, 0.9) 21.93%, rgba(119, 218, 255, 0.9) 78.19%)",
          }}
        >
          <Typography align="center" variant="h5" color="common.white">
            <FormattedMessage id="exercise.receiveExperience" />
          </Typography>

          <Box
            sx={{
              background: "#FFFFFF",
              height: 80,
              border: "1px solid #D9E2EB",
              borderRadius: "100px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography align="center" variant="h2" color="green.main">
              + {resultExercise?.point}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-around",
            }}
          >
            <Box>
              <Box
                component="img"
                sx={{
                  width: { xs: 70, sm: 80, md: 100 },
                  height: { xs: 70, sm: 80, md: 100 },
                }}
                alt="current"
                src={`/levels/Artboard – ${resultExercise?.experience_level}.png`}
              />
              <Typography
                align="center"
                sx={{ typography: { xs: "body2", sm: "body1" } }}
                color="white"
              >
                <FormattedMessage id="exercise.level" />{" "}
                {resultExercise?.experience_level}
              </Typography>
            </Box>

            <Box
              sx={{
                alignSelf: "center",
                flexGrow: 0.7,
              }}
            >
              <BorderLinearProgress variant="determinate" value={percent} />
            </Box>

            <Box>
              <Box
                component="img"
                sx={{
                  width: { xs: 70, sm: 80, md: 100 },
                  height: { xs: 70, sm: 80, md: 100 },
                }}
                alt="next"
                src={`/levels/Artboard – ${resultExercise?.next_experience_level}.png`}
              />

              <Typography
                align="center"
                sx={{ typography: { xs: "body2", sm: "body1" } }}
                color="white"
              >
                <FormattedMessage id="exercise.level" />{" "}
                {resultExercise?.next_experience_level}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              background: "#FFFFFF",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              borderRadius: "10px",
              padding: "20px 20px",
              gap: "20px",
            }}
          >
            <Typography
              align="center"
              variant="subtitle2"
              sx={{ fontWeight: 600 }}
              color="common.black"
            >
              <FormattedMessage id="exercise.levelTotal" />
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: "10px",
              }}
            >
              <Typography
                align="center"
                alignSelf="normal"
                variant="h2"
                color="green.main"
              >
                {resultExercise?.experience_point}
              </Typography>

              <Typography
                align="center"
                alignSelf="center"
                variant="h2"
                color="cgrey.main"
              >
                / {resultExercise?.next_experience_point}
              </Typography>
            </Box>

            <Typography align="center" variant="body1" color="cgrey.main">
              <FormattedMessage id="exercise.exp" />
            </Typography>
          </Box>

          <Button
            color="green"
            variant="contained"
            type="submit"
            sx={{ boxShadow: "0px 0px 6px rgba(0, 0, 0, 0.15)" }}
            component={RouterLink}
            to="/dashboard-exercise"
          >
            <Typography variant="subtitle2" color="white">
              <FormattedMessage id="exercise.result" />
            </Typography>
          </Button>

          <Button
            color="blue"
            variant="contained"
            type="submit"
            sx={{ boxShadow: "0px 0px 6px rgba(0, 0, 0, 0.15)" }}
            component={RouterLink}
            to="/home"
          >
            <Typography variant="subtitle2" color="white">
              <FormattedMessage id="home" />
            </Typography>
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default ResultExercise;
